// deps
import React, { useEffect, useState, memo, useCallback, useContext, ReactElement, useMemo } from "react";

// internal-deps
import { TickScreen, TICK_ACCENT, TICK_STATES } from "@swiggy-private/payment-ui";
import { CreatePennyTransactionResponse } from "@swiggy-private/payment-types";
import {
    CheckoutContext,
    getBridgeSdk,
    CheckoutActionTypes,
    TransactionReqData,
} from "@swiggy-private/checkout-core";

// hooks
import useConfirmTransaction from "@payment-client/hooks/UseConfirmTransaction";
import { useEffectOnce } from "@swiggy-private/core-ui";

// interfaces
import { RouteProps } from "@payment-client/interfaces/Router";

// helpers
import { cleanProcessTransactionState, getProcessTransactionState } from "@payment-client/helpers/CheckoutStateHelper";
import { shouldPollForPennyTxnStatus } from "@payment-client/helpers/PollingHelpers";

// constants
import {
    ORDER_CONFIRM_LOADING_SUB_TEXT,
    ORDER_CONFIRM_LOADING_TEXT,
    SUCCESS_TEXT,
} from "@payment-client/constants/ConfirmScreen";

//Analytics
import { AnalyticsContext } from "@swiggy-private/checkout-core";
import { onSecureCardSuccessView } from "@payment-client/analytics/events/Card";

interface ConfirmOrderContentProps {
    orderId: string;
    transactionData: CreatePennyTransactionResponse;
    transactionReqData: TransactionReqData;
    useJuspaySDK: boolean;
}

interface GetSuccessTextParams {
    loading: boolean;
    isNonSecureCard: boolean;
}

// TODO: Consume from config based on order
const POLLING_INTERVAL = 1000; // 1 secs
const POLLING_DURATION = 10000; // 10 secs

const getSuccessText = (
    params: GetSuccessTextParams,
): {
    mainText: string;
    subText?: string;
} => {
    const { loading, isNonSecureCard } = params;
    if (loading) {
        return {
            mainText: ORDER_CONFIRM_LOADING_TEXT,
            subText: ORDER_CONFIRM_LOADING_SUB_TEXT,
        };
    }
    return {
        mainText: isNonSecureCard ? SUCCESS_TEXT.CARD_SAVED : SUCCESS_TEXT.CARD_SECURED,
    };
};

export function ConfirmTransaction(_props: RouteProps): ReactElement | null {
    const { checkoutState, setCheckoutState } = useContext(CheckoutContext);

    useEffectOnce(() => {
        getBridgeSdk()
            .setScreenCaptureState({ screenCapturingDisabled: false })
            .catch(() => void 0);
    });

    useEffect(() => {
        if (checkoutState.type === CheckoutActionTypes.CONFIRM_TRANSACTION) {
            return;
        }

        const processTransactionState = getProcessTransactionState();
        if (!processTransactionState) {
            setCheckoutState({ type: CheckoutActionTypes.SECURE_CARD });
            return;
        }

        if (shouldPollForPennyTxnStatus(processTransactionState.state.transaction)) {
            setCheckoutState({
                type: CheckoutActionTypes.PENNY_TXN_STATUS_POLLER,
                payload: {
                    transaction: processTransactionState.state.transaction,
                    transactionReqData: processTransactionState.state.transactionReqData,
                    orderId: processTransactionState.orderId,
                    useJuspaySDK: processTransactionState.state.useJuspaySDK,
                },
            });
            cleanProcessTransactionState();
            return;
        }

        setCheckoutState({
            type: CheckoutActionTypes.CONFIRM_TRANSACTION,
            payload: {
                transaction: processTransactionState.state.transaction,
                transactionReqData: processTransactionState.state.transactionReqData,
                orderId: processTransactionState.orderId,
            },
        });

        // clean the data from storage
        cleanProcessTransactionState();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (checkoutState.type !== CheckoutActionTypes.CONFIRM_TRANSACTION) {
        return null;
    }

    return (
        <ConfirmTransactionContent
            orderId={checkoutState.payload.orderId}
            transactionData={checkoutState.payload.transaction as CreatePennyTransactionResponse}
            transactionReqData={checkoutState.payload.transactionReqData}
            useJuspaySDK={checkoutState.payload.useJuspaySDK ?? false}
        />
    );
}

const ConfirmTransactionContent = memo(function ConfirmOrderContent(
    props: ConfirmOrderContentProps,
): React.ReactElement {
    const { screenViewEvent } = useContext(AnalyticsContext);

    const [isOrderConfirmed] = useConfirmTransaction({
        pollingDurationInMs: POLLING_DURATION,
        pollingIntervalInMs: POLLING_INTERVAL,
        isWebPollingEnabled: shouldPollForPennyTxnStatus(props.transactionData),
    });

    const initialState = isOrderConfirmed ? TICK_STATES.SUCCESS : TICK_STATES.LOADING;
    const [state, setState] = useState<TICK_STATES>(initialState);

    const postConfirmOrderHandler = useCallback((): void => {
        onSecureCardSuccessView(screenViewEvent);
        getBridgeSdk()
            .close()
            .catch(() => void 0);
    }, [screenViewEvent]);

    const isNonSecureCard = useMemo(() => {
        return props.transactionReqData?.isNonSecureCard ?? false;
    }, [props.transactionReqData]);

    const successData = useMemo(
        () =>
            getSuccessText({
                loading: state === TICK_STATES.LOADING,
                isNonSecureCard,
            }),
        [isNonSecureCard, state],
    );

    useEffect(() => {
        if (state != TICK_STATES.SUCCESS && isOrderConfirmed) {
            setState(TICK_STATES.SUCCESS);

            getBridgeSdk()
                .transactionConfirmedEvent({})
                .catch(() => void 0);
        }
    }, [isOrderConfirmed, state]);

    return (
        <TickScreen
            tickState={state}
            mainText={successData.mainText}
            subText={successData.subText}
            postSuccessCb={postConfirmOrderHandler}
            accent={TICK_ACCENT.GREEN}
        />
    );
});
